import PropTypes from 'prop-types'
import { Fragment, useRef } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import {
  BlockGrid,
  Container,
  Inline,
  ListItem,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { useTheme } from '@emotion/react'
import { gradient, link, text } from 'chaoskit/src/assets/styles/utility'
import { motion } from 'framer-motion'

import Data from '~components/Data'
import CustomerLogos from '~partials/global/CustomerLogos'
import { stickyIt } from '~styles/utility'
import { ReactComponent as KVPReliabilitySvg } from '~media/product/overview/kvp-reliability.svg'
import { ReactComponent as KVPTrackSvg } from '~media/product/overview/kvp-track.svg'
import { ReactComponent as KVPTrustSvg } from '~media/product/overview/kvp-trust.svg'
import { ReactComponent as KVPAvoidSvg } from '~media/product/overview/kvp-avoid.svg'
import { ReactComponent as KVPReduceSvg } from '~media/product/overview/kvp-reduce.svg'
import { ReactComponent as VBFHAttacksSvg } from '~media/product/overview/vbfh-attacks.svg'
import { ReactComponent as VBFHScenariosSvg } from '~media/product/overview/vbfh-scenarios.svg'
import { ReactComponent as VBFHServicesDiscoverySvg } from '~media/product/overview/vbfh-services-discovery.svg'
import { ReactComponent as VBFHAPIsWebhooksSvg } from '~media/product/overview/vbfh-apis-webhooks.svg'
import { ReactComponent as VBFHAPIsStatusChecksSvg } from '~media/product/overview/vbfh-status-checks.svg'
import { ReactComponent as AWSSvg } from '~media/logos/aws.svg'
import { ReactComponent as AzureSvg } from '~media/logos/azure.svg'
import { ReactComponent as GoogleCloudServicesSvg } from '~media/logos/googlecloud.svg'
import { ReactComponent as LinuxSvg } from '~media/logos/linux.svg'
import { ReactComponent as WindowsSvg } from '~media/logos/windows.svg'
import { ReactComponent as KubernetesSvg } from '~media/logos/kubernetes.svg'
import { ReactComponent as LeastPermissionsSvg } from '~media/product/overview/least-permissions.svg'
import { ReactComponent as SecureUserManagementSvg } from '~media/product/overview/secure-user-management.svg'
import { ReactComponent as AuditTrailsSvg } from '~media/product/overview/audit-trails.svg'
import { ReactComponent as PartyTestingSvg } from '~media/product/overview/3rd-party-testing.svg'
import { ReactComponent as BlackholeSvg } from '~media/attacks/blackhole.svg'
import { ReactComponent as CPUSvg } from '~media/attacks/cpu.svg'
import { ReactComponent as DiskSvg } from '~media/attacks/disk.svg'
import { ReactComponent as DNSSvg } from '~media/attacks/dns.svg'
import { ReactComponent as IOSvg } from '~media/attacks/io.svg'
import { ReactComponent as LatencySvg } from '~media/attacks/latency.svg'
import { ReactComponent as MemorySvg } from '~media/attacks/memory.svg'
import { ReactComponent as PacketLossSvg } from '~media/attacks/packet-loss.svg'
import { ReactComponent as ProcessKillerSvg } from '~media/attacks/process-killer.svg'
import { ReactComponent as ShutdownSvg } from '~media/attacks/shutdown.svg'
import { ReactComponent as TimeTravelSvg } from '~media/attacks/time-travel.svg'
import ButtonLink from '~components/ButtonLink'
import useReveal from '~hooks/useReveal'

const KeyValueProp = ({ data }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref, direction: 'down' })

  let KVPIcon

  switch (data.feature) {
    case 'reliability':
      KVPIcon = KVPReliabilitySvg
      break
    case 'track':
      KVPIcon = KVPTrackSvg
      break
    case 'trust':
      KVPIcon = KVPTrustSvg
      break
    case 'avoid':
      KVPIcon = KVPAvoidSvg
      break
    case 'reduce':
      KVPIcon = KVPReduceSvg
      break
    default:
      KVPIcon = () => null
  }

  return (
    <ListItem>
      <motion.div
        css={{
          display: 'grid',
          gap: theme.space.small,
          justifyContent: 'center',
          textAlign: 'center',
          alignSelf: 'start',
        }}
        ref={ref}
        variants={variants}
        animate={controls}
        initial="hidden"
      >
        <KVPIcon css={{ height: 80, display: 'block', margin: 'auto' }} />
        <div
          css={{
            ...theme.text.large,
            fontWeight: theme.fontWeight.bold,
          }}
        >
          {data.heading}
        </div>
        <div css={{ ...theme.text.small }}>
          <Data>{data.blurb}</Data>
        </div>
      </motion.div>
    </ListItem>
  )
}

KeyValueProp.propTypes = {
  data: PropTypes.object.isRequired,
}

const ValueBasedFeatureHighlight = ({ highlight, length, index }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  let VBFHImage

  switch (highlight.feature) {
    case 'attacks':
      VBFHImage = VBFHAttacksSvg
      break
    case 'scenarios':
      VBFHImage = VBFHScenariosSvg
      break
    case 'services-discovery':
      VBFHImage = VBFHServicesDiscoverySvg
      break
    case 'status-checks':
      VBFHImage = VBFHAPIsStatusChecksSvg
      break
    case 'apis-webhooks':
      VBFHImage = VBFHAPIsWebhooksSvg
      break
    default:
      VBFHImage = () => null
  }

  return (
    <motion.div
      ref={ref}
      variants={variants}
      animate={controls}
      initial="hidden"
      css={{
        textAlign: 'center',
        display: 'grid',
        gap: theme.space.large,
        marginBottom: index === length - 1 ? null : theme.space.xlarge,

        [theme.mq.medium]: {
          textAlign: 'left',
          gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
          gap: theme.space.xlarge,
          marginBottom: index === length - 1 ? null : theme.space['2xlarge'],
        },
      }}
    >
      <div
        css={{
          [theme.mq.medium]: {
            gridColumn: index % 2 === 0 ? '1 / span 7' : '6 / span 7',
            gridRow: 1,
            direction: index % 2 === 0 ? 'rtl' : null,
            order: index % 2 === 0 ? null : 1,
          },
        }}
      >
        <VBFHImage
          css={{
            [theme.mq.small__max]: {
              maxWidth: 400,
            },

            [theme.mq.medium]: {
              maxWidth: 'none',
              width: 600,
              marginTop: '-5%',
            },
          }}
        />
      </div>
      <div
        css={{
          [theme.mq.medium]: {
            gridColumn: index % 2 === 0 ? '8 / span 5' : '1 / span 5',
            gridRow: 1,
          },
        }}
      >
        <h3>
          <Data>{highlight.heading}</Data>
        </h3>
        <div>
          <Data>{highlight.blurb}</Data>
        </div>
      </div>
    </motion.div>
  )
}

ValueBasedFeatureHighlight.propTypes = {
  highlight: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    feature: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
  }),
  length: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
}

const SupportedPlatforms = ({ data }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <Section ref={ref} variants={variants} animate={controls} initial="hidden">
      <SectionTitle
        as="h2"
        title={<Data>{data.heading}</Data>}
        sub={<Data>{data.blurb}</Data>}
        css={{
          '.CK__SectionTitle__Sub': {
            a: [text.underline, link.reset(theme)],
          },
        }}
      />
      <Inline size="xlarge" css={{ justifyContent: 'center' }}>
        <ListItem>
          <AWSSvg css={{ height: 50 }} title="AWS" />
        </ListItem>
        <ListItem>
          <AzureSvg css={{ height: 40 }} title="Azure" />
        </ListItem>
        <ListItem>
          <GoogleCloudServicesSvg
            css={{ height: 50 }}
            title="Google Cloud Services"
          />
        </ListItem>
        <ListItem>
          <LinuxSvg css={{ height: 60 }} title="Linux" />
        </ListItem>
        <ListItem>
          <WindowsSvg css={{ height: 50 }} title="Windows" />
        </ListItem>
        <ListItem>
          <KubernetesSvg css={{ height: 60 }} title="Kubernetes" />
        </ListItem>
      </Inline>
    </Section>
  )
}

SupportedPlatforms.propTypes = {
  data: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
  }),
}

const Attack = ({ attack }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  let AttackIcon

  switch (attack.heading.toLowerCase()) {
    case 'blackhole':
      AttackIcon = BlackholeSvg
      break
    case 'cpu':
      AttackIcon = CPUSvg
      break
    case 'disk':
      AttackIcon = DiskSvg
      break
    case 'dns':
      AttackIcon = DNSSvg
      break
    case 'io':
      AttackIcon = IOSvg
      break
    case 'latency':
      AttackIcon = LatencySvg
      break
    case 'memory':
      AttackIcon = MemorySvg
      break
    case 'packet loss':
      AttackIcon = PacketLossSvg
      break
    case 'process killer':
      AttackIcon = ProcessKillerSvg
      break
    case 'shutdown':
      AttackIcon = ShutdownSvg
      break
    case 'time travel':
      AttackIcon = TimeTravelSvg
      break
    default:
      AttackIcon = () => null
  }

  return (
    <motion.div
      ref={ref}
      variants={variants}
      animate={controls}
      initial="hidden"
      css={{
        display: 'grid',
        justifyContent: 'start',
        gap: theme.space.base,
        textAlign: 'center',
        position: 'relative',
        zIndex: 1,

        '&::before': {
          content: "''",
          border: theme.border.base,
          top: -theme.space.base,
          left: -theme.space.base,
          right: -theme.space.base,
          bottom: -theme.space.base,
          position: 'absolute',
          zIndex: -1,
          boxShadow: theme.boxShadow.base,
          borderRadius: theme.borderRadius.base,
          opacity: 0,
          transition: `transform ${theme.timing.base} ${theme.transition.bounce}, opacity ${theme.timing.base} ${theme.transition.bounce}`,
          willChange: 'transform',
          transform: 'scale(0.95)',
          transformOrigin: 'center center',
        },

        '&:hover': {
          '::before': {
            opacity: 1,
            transform: 'scale(1)',
          },
        },

        [theme.mq.medium]: {
          gridAutoColumns: 'auto',
          gridAutoFlow: 'column',
          textAlign: 'left',
        },
      }}
    >
      <div
        css={{
          width: 80,
          height: 80,
          ...gradient.dark(theme),
          borderRadius: 9999,
          boxShadow: theme.boxShadow.large,
          display: 'grid',
          placeContent: 'center',
          margin: 'auto',
        }}
      >
        <AttackIcon
          css={{
            color: theme.color.panel.base,
            width: theme.space.large + theme.space.base,
          }}
        />
      </div>
      <div css={{ display: 'grid', gap: theme.space.small }}>
        <div
          css={{
            ...theme.text.large,
            fontWeight: theme.fontWeight.bold,
          }}
        >
          <Data>{attack.heading}</Data>
        </div>
        <div css={{ ...theme.text.small }}>
          <Data>{attack.blurb}</Data>
        </div>
      </div>
    </motion.div>
  )
}

Attack.propTypes = {
  attack: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
  }),
}

const AttackLibrary = ({ data }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <Section
      id="attack-library"
      ref={ref}
      variants={variants}
      animate={controls}
      initial="hidden"
    >
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,

          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: theme.space.xlarge,
          },
        }}
      >
        <div
          css={{
            [theme.mq.medium]: {
              gridColumn: '1 / span 5',
              gridRow: 1,
            },
          }}
        >
          <div css={{ [theme.mq.medium]: stickyIt(theme) }}>
            <SectionTitle
              as="h2"
              align={{ medium: 'left' }}
              title={<Data>{data.heading}</Data>}
              sub={<Data>{data.blurb}</Data>}
              css={{
                // @NOTE Force line-break on larger screens and remove on mobile
                [theme.mq.small__max]: {
                  br: {
                    display: 'none',
                  },
                },
              }}
            />
          </div>
        </div>
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridColumn: '6 / span 7',
              gridRow: 1,
            },
          }}
        >
          {data.items.map((attack) => (
            <Attack key={attack.heading} attack={attack} />
          ))}
        </div>
      </div>
    </Section>
  )
}

AttackLibrary.propTypes = {
  data: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }),
}

const SecurityItem = ({ data }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  let SecureIcon

  switch (data.icon) {
    case 'leastPermissions':
      SecureIcon = LeastPermissionsSvg
      break
    case 'secureUserManagement':
      SecureIcon = SecureUserManagementSvg
      break
    case 'auditTrails':
      SecureIcon = AuditTrailsSvg
      break
    case 'partyTesting':
      SecureIcon = PartyTestingSvg
      break
    default:
      SecureIcon = () => null
  }

  return (
    <ListItem>
      <motion.div
        ref={ref}
        variants={variants}
        animate={controls}
        initial="hidden"
        css={{
          display: 'grid',
          gap: theme.space.small,
          alignSelf: 'start',
          textAlign: 'center',

          [theme.mq.medium]: {
            textAlign: 'left',
          },
        }}
      >
        <SecureIcon
          css={{
            height: 80,
            margin: 'auto',

            [theme.mq.medium]: { margin: 0 },
          }}
        />
        <div
          css={{
            ...theme.text.large,
            fontWeight: theme.fontWeight.bold,
          }}
        >
          <Data>{data.heading}</Data>
        </div>
        <div css={{ ...theme.text.small }}>
          <Data>{data.blurb}</Data>
        </div>
      </motion.div>
    </ListItem>
  )
}

SecurityItem.propTypes = {
  data: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
  }),
}

const Security = ({ data }) => {
  const theme = useTheme()

  const ref = useRef()

  const { variants, controls } = useReveal({ ref })

  return (
    <Section ref={ref} variants={variants} animate={controls} initial="hidden">
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,

          [theme.mq.medium]: {
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: theme.space.xlarge,
          },
        }}
      >
        <div
          css={{
            [theme.mq.medium]: {
              gridColumn: '1 / span 5',
              gridRow: 1,
            },
          }}
        >
          <div css={{ [theme.mq.medium]: stickyIt(theme) }}>
            <SectionTitle
              as="h2"
              align={{ medium: 'left' }}
              title={<Data>{data.heading}</Data>}
              sub={<Data>{data.blurb}</Data>}
              css={{ marginBottom: `${theme.space.large}px !important` }}
            />
            <div
              css={{
                textAlign: 'center',

                [theme.mq.medium]: {
                  textAlign: 'left',
                },
              }}
            >
              <ButtonLink url="/security?ref=product" type="secondary">
                Learn more
              </ButtonLink>
            </div>
          </div>
        </div>
        <div
          css={{
            [theme.mq.medium]: {
              gridColumn: '6 / span 7',
              gridRow: 1,
            },
          }}
        >
          <BlockGrid size={{ medium: 2 }} gutter={{ base: 'large' }}>
            {data.items.map((securityItem) => (
              <SecurityItem key={securityItem.heading} data={securityItem} />
            ))}
          </BlockGrid>
        </div>
      </div>
    </Section>
  )
}

Security.propTypes = {
  data: PropTypes.shape({
    blurb: PropTypes.string.isRequired,
    heading: PropTypes.string.isRequired,
    items: PropTypes.array.isRequired,
  }),
}

const ProductOverview = () => {
  const theme = useTheme()

  const {
    file: {
      childMdx: { frontmatter: pageData },
    },
  } = useStaticQuery(graphql`
    query ProductOverviewPageData {
      file(name: { eq: "product" }, absolutePath: { regex: "/src/data/" }) {
        childMdx {
          frontmatter {
            keyValueProps {
              heading
              feature
              blurb
            }
            valueBasedFeatureHighlights {
              heading
              feature
              blurb
            }
            supportedPlatforms {
              heading
              blurb
            }
            attackLibrary {
              heading
              blurb
              items {
                heading
                blurb
              }
            }
            productSecurity {
              heading
              blurb
              items {
                heading
                blurb
                icon
              }
            }
            productSocialProof {
              heading
              blurb
            }
          }
        }
      }
    }
  `)

  return (
    <Fragment>
      <Container>
        <Section>
          <BlockGrid
            size={{ medium: 2, large: 3 }}
            gutter={{ base: 'large', large: 'xlarge' }}
            css={{ justifyContent: 'center' }}
          >
            {pageData.keyValueProps.map((prop) => (
              <KeyValueProp key={prop.heading} data={prop} />
            ))}
          </BlockGrid>
        </Section>

        <Section>
          {pageData.valueBasedFeatureHighlights.map((highlight, index) => (
            <ValueBasedFeatureHighlight
              key={highlight.feature}
              highlight={highlight}
              length={pageData.valueBasedFeatureHighlights.length}
              index={index}
            />
          ))}
        </Section>
        <SupportedPlatforms data={pageData.supportedPlatforms} />
        <AttackLibrary data={pageData.attackLibrary} />
        <Security data={pageData.productSecurity} />
      </Container>

      <Section
        css={{
          ...gradient.dark(theme),
          position: 'relative',
          paddingBottom: `${theme.space.xlarge}px !important`,
        }}
        space="xlarge"
        className="u-contrast"
      >
        <hr
          css={{
            margin: 0,
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
          }}
        />
        <svg
          css={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: 100,
            display: 'none',
            zIndex: 2,

            [theme.mq.medium]: {
              display: 'block',
            },
          }}
          fill="#fff"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon
            css={{ filter: `drop-shadow(0 0 5px ${theme.color.dark.base})` }}
            points="100,0 0,100 0,0"
          />
        </svg>
        <Container>
          <SectionTitle
            as="h2"
            title={<Data>{pageData.productSocialProof.heading}</Data>}
            sub={<Data>{pageData.productSocialProof.blurb}</Data>}
          />
        </Container>
        <CustomerLogos contrast />
      </Section>
    </Fragment>
  )
}

export default ProductOverview
